import * as React from 'react';
import {
  ALLURE_TABLE,
  zoneDepuisApi,
  zoneVersApi,
  type ZoneRow,
  type ZoneRowApi,
} from '../Construction/allureTable.ts';

// Parametres d'allures de l'utilisateur courant (table de zones utilisee
// par Construction et Modeles), stockes cote serveur par utilisateur
// (UserPreferenceController : GET/PUT/DELETE /api/preference/allures).
//
// Comme useVma, on garde une copie en sessionStorage pour disposer
// immediatement de la derniere table connue pendant le chargement ; a
// defaut, c'est la table par defaut ALLURE_TABLE qui s'applique.
const ALLURE_TABLE_STORAGE_KEY = 'allureTable';

// Evenement emis apres un enregistrement / une reinitialisation des
// parametres d'allures : le serveur a pu recalculer la VMA (si le %VMA de la
// zone du dernier record a change), les ecrans qui l'affichent la
// rechargent (cf Preferences/VmaRecord.tsx).
export const PARAMETRES_ALLURES_MODIFIES_EVENT = 'app:parametres-allures-modifies';

function signalerModification(): void {
  window.dispatchEvent(new Event(PARAMETRES_ALLURES_MODIFIES_EVENT));
}

type AllureTableApi = {
  personnalisee: boolean;
  zones: ZoneRowApi[];
};

function chargerCache(): { table: ZoneRow[]; personnalisee: boolean } | undefined {
  try {
    const raw = sessionStorage.getItem(ALLURE_TABLE_STORAGE_KEY);
    if (!raw) return undefined;
    const data = JSON.parse(raw) as AllureTableApi;
    return { table: data.zones.map(zoneDepuisApi), personnalisee: data.personnalisee };
  } catch {
    return undefined;
  }
}

function sauverCache(data: AllureTableApi): void {
  try {
    sessionStorage.setItem(ALLURE_TABLE_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage indisponible : on ignore silencieusement
  }
}

function requete(method: 'GET' | 'PUT' | 'DELETE', athlete: string | null, body?: unknown): Request {
  const token = sessionStorage.getItem('token');
  const url = '/api/preference/allures' + (athlete ? '?athlete=' + encodeURIComponent(athlete) : '');
  return new Request(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function lireReponse(response: Response): Promise<AllureTableApi> {
  if (response.status === 200) {
    return (await response.json()) as AllureTableApi;
  }
  let message = 'statut ' + response.status;
  try {
    const erreur = await response.json();
    if (erreur?.message) message = erreur.message;
  } catch {
    // corps non JSON : on garde le statut
  }
  throw new Error(message);
}

export type UseAllureTable = {
  /** Table a utiliser pour les calculs (preferences ou valeurs par defaut). */
  table: ZoneRow[];
  /** true si l'utilisateur a enregistre ses propres parametres. */
  personnalisee: boolean;
  chargement: boolean;
  /** Enregistre la table complete ; rejette avec le message du serveur si invalide. */
  enregistrer: (table: ZoneRow[]) => Promise<void>;
  /** Supprime la personnalisation et revient aux valeurs par defaut. */
  reinitialiser: () => Promise<void>;
};

// athlete : parametres d'un athlete rattache, que son coach consulte et
// modifie (page Athletes) ; sans cache, et sans prevenir les ecrans du coach.
// Seul le coach modifie des parametres d'allures (409 pour un athlete).
export function useAllureTable(athlete: string | null = null): UseAllureTable {
  const cache = React.useMemo(() => (athlete ? undefined : chargerCache()), [athlete]);
  const [table, setTable] = React.useState<ZoneRow[]>(cache?.table ?? ALLURE_TABLE);
  const [personnalisee, setPersonnalisee] = React.useState<boolean>(cache?.personnalisee ?? false);
  const [chargement, setChargement] = React.useState<boolean>(true);

  const appliquer = React.useCallback((data: AllureTableApi) => {
    setTable(data.zones.map(zoneDepuisApi));
    setPersonnalisee(data.personnalisee);
    if (!athlete) sauverCache(data);
  }, [athlete]);
  const signaler = React.useCallback(() => {
    if (!athlete) signalerModification();
  }, [athlete]);

  React.useEffect(() => {
    setChargement(true);
    fetch(requete('GET', athlete))
      .then(lireReponse)
      .then(appliquer)
      .catch((error) => {
        // Serveur injoignable ou utilisateur non authentifie : on garde
        // la derniere table connue / la table par defaut.
        console.warn('Impossible de récupérer les paramètres d\'allures, valeurs par défaut utilisées', error);
      })
      .finally(() => setChargement(false));
  }, [appliquer, athlete]);

  const enregistrer = React.useCallback(
    (nouvelle: ZoneRow[]) =>
      fetch(requete('PUT', athlete, nouvelle.map(zoneVersApi)))
        .then(lireReponse)
        .then(appliquer)
        .then(signaler),
    [appliquer, signaler, athlete],
  );

  const reinitialiser = React.useCallback(
    () =>
      fetch(requete('DELETE', athlete))
        .then(lireReponse)
        .then(appliquer)
        .then(signaler),
    [appliquer, signaler, athlete],
  );

  return { table, personnalisee, chargement, enregistrer, reinitialiser };
}
